import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Alert,
  Stack,
  TextField,
  FormControlLabel,
  Switch,
  Link as MuiLink,
} from '@mui/material';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyText } from '../lib/clipboard.js';
import { SdkInstall } from '../components/SdkInstall.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

export function ApiKeysPage() {
  const t = useT().keys.apiKeys;
  const access = useT().keys.access;
  const { projectId } = useParams();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const keys = useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['apikeys', projectId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}/api-keys`)).data.data as Array<{
        id: string;
        name: string;
        keyPrefix: string;
        key: string | null;
        lastUsedAt: string | null;
        revokedAt: string | null;
        createdAt: string;
      }>,
  });

  const createKey = useMutation({
    mutationFn: async (keyName: string) =>
      (
        await api.post(`/v1/orgs/${orgId}/projects/${projectId}/api-keys`, {
          name: keyName,
          environment: 'live',
        })
      ).data,
    onSuccess: (data) => {
      setRevealed(data.key);
      setName('');
      qc.invalidateQueries({ queryKey: ['apikeys', projectId] });
    },
  });

  const activeKeys = keys.data?.filter((k) => !k.revokedAt) ?? [];
  // Prefer the newest active "default" key. Ingest keys are public, so we can
  // pre-fill the snippet from its stored value without re-issuing anything.
  const defaultKey = activeKeys
    .filter((k) => k.name === 'default')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const hasDefault = !!defaultKey;
  // What the snippet shows: a just-created key, else the existing default's value.
  const snippetKey = revealed ?? defaultKey?.key ?? null;
  const revokedCount = keys.data?.filter((k) => k.revokedAt).length ?? 0;
  const visibleKeys = (keys.data ?? []).filter((k) => showRevoked || !k.revokedAt);
  const sort = useSort<'name' | 'prefix' | 'lastUsed' | 'status'>('lastUsed');
  const rows = sort.sorted(visibleKeys, {
    name: (k) => k.name,
    prefix: (k) => k.keyPrefix,
    lastUsed: (k) => k.lastUsedAt,
    // Active first when descending: a revoked key is the exception.
    status: (k) => (k.revokedAt ? 0 : 1),
  });

  /**
   * Fills the snippet with a working key. A raw key can only be read at
   * creation, so we keep a single "default" slot: if one (or more) already
   * exist we revoke them and mint one fresh "default" (rotate) rather than
   * piling up duplicates. If none exists we just create it.
   */
  const fillSnippet = useMutation({
    mutationFn: async () => {
      const staleDefaults = activeKeys.filter((k) => k.name === 'default');
      for (const k of staleDefaults) {
        await api.delete(`/v1/orgs/${orgId}/projects/${projectId}/api-keys/${k.id}`);
      }
      return (
        await api.post(`/v1/orgs/${orgId}/projects/${projectId}/api-keys`, {
          name: 'default',
          environment: 'live',
        })
      ).data;
    },
    onSuccess: (data) => {
      setRevealed(data.key);
      qc.invalidateQueries({ queryKey: ['apikeys', projectId] });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/v1/orgs/${orgId}/projects/${projectId}/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['apikeys', projectId] }),
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t.title}
      </Typography>

      {revealed && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRevealed(null)}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <span style={{ wordBreak: 'break-all' }}>
              {t.revealed}&nbsp;<code>{revealed}</code>
            </span>
            <Button
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={async () => {
                await copyText(revealed);
                setCopiedKey('revealed');
                setTimeout(() => setCopiedKey(null), 1500);
              }}
            >
              {copiedKey === 'revealed' ? t.copied : t.copy}
            </Button>
          </Stack>
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label={t.keyName}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button variant="contained" disabled={!name} onClick={() => createKey.mutate(name)}>
          {t.createKey}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {revokedCount > 0 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRevoked}
                onChange={(e) => setShowRevoked(e.target.checked)}
              />
            }
            label={t.showRevoked(revokedCount)}
          />
        )}
      </Stack>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              {sort.head('name', t.columns.name)}
              {sort.head('prefix', t.columns.prefix)}
              {sort.head('lastUsed', t.columns.lastUsed)}
              {sort.head('status', t.columns.status)}
              <TableCell align="right">{t.columns.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell>
                  <code>{k.keyPrefix}…</code>
                </TableCell>
                <TableCell>{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : t.never}</TableCell>
                <TableCell>{k.revokedAt ? t.revoked : t.active}</TableCell>
                <TableCell align="right">
                  {/* Ingest keys are public by design (their raw value is stored),
                      so the full key stays copyable after creation. */}
                  {!k.revokedAt && k.key && (
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      onClick={async () => {
                        await copyText(k.key!);
                        setCopiedKey(k.id);
                        setTimeout(() => setCopiedKey(null), 1500);
                      }}
                    >
                      {copiedKey === k.id ? t.copied : t.copy}
                    </Button>
                  )}
                  {!k.revokedAt && (
                    <Button color="error" size="small" onClick={() => revoke.mutate(k.id)}>
                      {t.revoke}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <SdkInstall
          projectId={projectId!}
          apiKey={snippetKey}
          onGenerate={() => fillSnippet.mutate()}
          generating={fillSnippet.isPending}
          rotate={hasDefault}
        />
      </Box>

      {/* Server tokens and MCP are org-wide, so they live on their own page.
          A pointer stays here because this is where they used to be. */}
      <Alert severity="info" variant="outlined" sx={{ mt: 4, maxWidth: 760 }}>
        {access.movedFrom}{' '}
        <MuiLink component={RouterLink} to="/api-access">
          {access.title}
        </MuiLink>
        .
      </Alert>
    </Box>
  );
}
